/**
 * Loads the 7 real card icon images as base64 data URIs for jsPDF's
 * addImage. Default paths point at /assets/icons/*.png — drop your own
 * versions in at these same paths (in your app's public/ or assets/
 * directory) and no code change is needed.
 *
 * Standardized: 600x600px, rounded-corner chip, dark charcoal (#262626)
 * background, orange (#C2410C) icon art — except risk-extreme.png, which
 * is red (#7F1D1D) to match the "Extreme cases" card's existing red theme.
 */
export interface ReportIconSet {
  evictionHouseholds: string;
  evictionIndividuals: string;
  preventionHouseholds: string;
  preventionIndividuals: string;
  riskDistricts: string;
  riskTotalHouseholds: string;
  riskExtreme: string;
}

const ICON_FILENAMES: Record<keyof ReportIconSet, string> = {
  evictionHouseholds: 'eviction-households.png',
  evictionIndividuals: 'eviction-individuals.png',
  preventionHouseholds: 'prevention-households.png',
  preventionIndividuals: 'prevention-individuals.png',
  riskDistricts: 'risk-districts.png',
  riskTotalHouseholds: 'risk-total-households.png',
  riskExtreme: 'risk-extreme.png',
};

export async function loadReportIconsBase64(
  baseUrl = '/assets/icons',
): Promise<ReportIconSet> {
  const toDataUri = async (filename: string): Promise<string> => {
    const res = await fetch(`${baseUrl}/${filename}`);
    if (!res.ok) throw new Error(`Failed to load icon ${filename}: HTTP ${res.status}`);
    const blob = await res.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const entries = await Promise.all(
    (Object.entries(ICON_FILENAMES) as [keyof ReportIconSet, string][]).map(
      async ([key, filename]) => [key, await toDataUri(filename)] as const,
    ),
  );

  return Object.fromEntries(entries) as unknown as ReportIconSet;
}